import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useGraphIROs } from "../../../services/graph_data_adapter";
import { JourneySection, JourneyPanel } from "../../journeys/JourneyPanel";
import { MaterialityAssessment } from "../../journeys/JourneyMateriality";

const API = process.env.REACT_APP_BACKEND_URL;

const C={bg:"#050a14",surface:"#0c1220",card:"#111c30",cardHover:"#162040",border:"#1a2744",borderHi:"#2a3a5c",text:"#e2e8f0",muted:"#6b7a96",dim:"#384660",blue:"#3b82f6",amber:"#f59e0b",emerald:"#10b981",purple:"#8b5cf6",cyan:"#06b6d4",red:"#ef4444",pink:"#ec4899",teal:"#14b8a6",orange:"#f97316",lime:"#84cc16",white:"#fff",indigo:"#6366f1",rose:"#f43f5e"};
const OWNERS=["","ESG Controller","CSO","CFO","Legal","Operations","EHS","HR","Supply Chain","Risk","Internal Audit","Board","Sustainability Mgr","External Assurer"];
const ESRS={E1:{l:"Climate Change",p:"E",c:C.emerald},E2:{l:"Pollution",p:"E",c:C.orange},E3:{l:"Water & Marine",p:"E",c:C.cyan},E4:{l:"Biodiversity",p:"E",c:C.lime},E5:{l:"Resource Use",p:"E",c:C.teal},S1:{l:"Own Workforce",p:"S",c:C.blue},S2:{l:"Value Chain Workers",p:"S",c:C.indigo},S3:{l:"Communities",p:"S",c:C.pink},S4:{l:"Consumers",p:"S",c:C.rose},G1:{l:"Business Conduct",p:"G",c:C.purple}};
const PILS=[{id:"E",l:"Environment",c:C.emerald},{id:"S",l:"Social",c:C.blue},{id:"G",l:"Governance",c:C.purple}];
const STATUS_FLOW=["unassessed","draft","assessed","approved"];
const ST_M={unassessed:{c:C.dim,l:"Unassessed",nx:"draft"},draft:{c:C.blue,l:"Draft",nx:"assessed"},assessed:{c:C.amber,l:"Assessed",nx:"approved"},approved:{c:C.emerald,l:"Approved",nx:"unassessed"}};
const FW_T={E1:{gri:["GRI 302","GRI 305"],issb:["IFRS S2"],cdp:["C6 Emissions","C8 Energy"],tcfd:["MET-a","STRAT-b"]},E2:{gri:["GRI 305-7","GRI 306"],issb:[],cdp:[],tcfd:[]},E3:{gri:["GRI 303"],issb:[],cdp:["W1-W4 Water"],tcfd:[]},E4:{gri:["GRI 304"],issb:[],cdp:["F1-F4 Forests"],tcfd:[]},E5:{gri:["GRI 301","GRI 306"],issb:[],cdp:[],tcfd:[]},S1:{gri:["GRI 401-405","GRI 403"],issb:["IFRS S1 ¶25"],cdp:[],tcfd:[]},S2:{gri:["GRI 407-409","GRI 414"],issb:[],cdp:[],tcfd:[]},S3:{gri:["GRI 411","GRI 413"],issb:[],cdp:[],tcfd:[]},S4:{gri:["GRI 416","GRI 418"],issb:[],cdp:[],tcfd:[]},G1:{gri:["GRI 205-207"],issb:["IFRS S1 ¶26"],cdp:[],tcfd:["GOV-a","GOV-b"]}};
const THRESH={impact:9,financial:9};
const SEV_LABELS=["","Negligible","Minor","Moderate","Significant","Severe"];
const LIK_LABELS=["","Very Unlikely","Unlikely","Possible","Likely","Very Likely"];
const MAG_LABELS=["","Negligible","Low","Medium","High","Very High"];
const PROB_LABELS=["","Remote","Low","Medium","High","Very High"];

// ─── API helpers ──────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}
function hdrs() {
  return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` };
}
async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { ...hdrs(), ...opts.headers } });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

// Map backend → frontend shorthand
function fromAPI(d) {
  return {
    id: d.id,
    n: d.name,
    p: d.pillar,
    e: d.esrs,
    ty: d.type,
    iS: d.impact_severity || 0,
    iL: d.impact_likelihood || 0,
    fM: d.financial_magnitude || 0,
    fP: d.financial_probability || 0,
    ow: d.owner || "",
    st: d.status || "unassessed",
    ev: Array.isArray(d.evidence) ? d.evidence : (d.evidence ? [d.evidence] : []),
    no: d.notes || "",
    stk: Array.isArray(d.stakeholders) ? d.stakeholders : [],
    apBy: d.approved_by || "",
    apAt: d.approved_at || "",
    // Graph enrichment
    graphId: d.graph_id || null,
    graphVerdict: d.graph_verdict || null,
    activatedDPs: d.activated_datapoints || 0,
    triggeredESRS: d.triggered_esrs || 0,
    triggeredFWReqs: d.triggered_fw_reqs || 0,
  };
}

// Map frontend shorthand → backend payload
function toAPI(i) {
  return {
    name: i.n,
    pillar: i.p,
    esrs: i.e,
    type: i.ty,
    impact_severity: i.iS,
    impact_likelihood: i.iL,
    financial_magnitude: i.fM,
    financial_probability: i.fP,
    owner: i.ow,
    status: i.st,
    evidence: i.ev,
    notes: i.no,
    stakeholders: i.stk,
    approved_by: i.apBy,
    approved_at: i.apAt,
  };
}

// ─── Components ───────────────────────────────────────────────────
const Pl=({color:cl,children,small,onClick,active,glow,"data-testid":testId})=><span data-testid={testId} onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:3,padding:small?"1px 6px":"3px 9px",borderRadius:99,fontSize:small?8:10,fontWeight:600,background:active?cl+"22":cl+"10",color:cl,border:`1px solid ${active?cl+"66":cl+"22"}`,cursor:onClick?"pointer":"default",transition:"all .2s",boxShadow:glow?`0 0 8px ${cl}33`:"none"}}>{children}</span>;
const Bar=({v,mx,cl,h=5})=><div style={{width:"100%",height:h,borderRadius:99,background:C.border,overflow:"hidden"}}><div style={{width:Math.min((v/mx)*100,100)+"%",height:"100%",borderRadius:99,background:`linear-gradient(90deg,${cl}88,${cl})`,transition:"width .4s"}}/></div>;

function ScoreGrid({label,value,onChange,color,labels,"data-testid":testId}){
  return(<div data-testid={testId} style={{marginBottom:6}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
      <span style={{fontSize:9,color:C.muted}}>{label}</span>
      <span style={{fontSize:9,fontWeight:700,color:value>0?color:C.dim}}>{labels[value]||"—"}</span>
    </div>
    <div style={{display:"flex",gap:2}}>
      {[1,2,3,4,5].map(n=>(
        <button key={n} data-testid={`${testId}-btn-${n}`} onClick={()=>onChange(n)} style={{flex:1,height:28,borderRadius:5,fontSize:11,fontWeight:700,cursor:"pointer",background:n<=value?color+(n===value?"33":"18"):"transparent",color:n<=value?color:C.dim,border:`1px solid ${n<=value?color+"55":C.border}`,transition:"all .15s",position:"relative"}}>
          {n}
          {n===value&&<div style={{position:"absolute",bottom:-1,left:"50%",transform:"translateX(-50%)",width:"60%",height:2,borderRadius:1,background:color}}/>}
        </button>
      ))}
    </div>
  </div>);
}

// ─── MAIN ─────────────────────────────────────────────────────────
export default function MaterialityOS({ initialView }){
  // ── Graph-powered IRO data (primary: Neo4j, fallback: MongoDB) ──
  const { iros: rawIros, loading, error: graphError, dataSource, kpis: graphKpis, scoreIRO, toggleVerdict, previewCascade, refetch: refetchIros, wsConnected } = useGraphIROs();

  // Map graph adapter shape → existing frontend shorthand
  const [iros, setIros] = useState([]);
  useMemo(() => {
    setIros(rawIros.map(g => ({
      id: g.id, n: g.name, p: g.pillar, e: g.esrs_topic, ty: "impact",
      iS: g.iSev, iL: g.iLik, fM: g.fMag, fP: g.fLik,
      ow: g.owner || "", st: "assessed", ev: [], no: g.rationale || "", stk: [],
      apBy: "", apAt: "",
      graphId: g.id, graphVerdict: g.verdict,
      activatedDPs: g.dpCount, triggeredESRS: g.esrsCount, triggeredFWReqs: g.fwCount,
    })));
  }, [rawIros]);

  const [sel,setSel]=useState(null);
  const [view,setView]=useState(initialView || "work");

  // Sync view when navigating between sub-routes (initialView prop changes)
  useEffect(()=>{
    if(initialView) setView(initialView);
  },[initialView]);
  const [pilF,setPilF]=useState(null);
  const [showAdd,setShowAdd]=useState(false);
  const [newName,setNewName]=useState("");
  const [newP,setNewP]=useState("E");
  const [newE,setNewE]=useState("E1");
  const [newTy,setNewTy]=useState("impact");
  const [newEvInput,setNewEvInput]=useState("");
  const [newStkInput,setNewStkInput]=useState("");
  const debounceRef = useRef({});

  // ── Persist a single field change — graph for scores, local for workflow ──
  const persistField = useCallback((id, field, value, immediate=true)=>{
    setIros(p=>p.map(i=>{
      if(i.id!==id)return i;
      const updated={...i,[field]:value};
      if(field==="st" && value==="approved"){
        updated.apBy="You";
        updated.apAt=new Date().toISOString().slice(0,10);
      }
      return updated;
    }));

    const doUpdate = ()=>{
      setIros(prev=>{
        const item = prev.find(i=>i.id===id);
        if(item) {
          // Score fields go to graph endpoint (triggers cascade)
          if(["iS","iL","fM","fP"].includes(field)) {
            scoreIRO(id, { iSev: item.iS, iLik: item.iL, fMag: item.fM, fLik: item.fP });
          } else {
            // Workflow fields: try MongoDB endpoint
            const payload = toAPI(item);
            apiFetch(`/api/v1/materiality/iros/${id}`,{method:"PUT",body:JSON.stringify(payload)}).catch(()=>{});
          }
        }
        return prev;
      });
    };

    if(immediate){
      // Small delay to let state settle, then persist
      setTimeout(doUpdate, 50);
    } else {
      // Debounce for text fields
      if(debounceRef.current[id+field]) clearTimeout(debounceRef.current[id+field]);
      debounceRef.current[id+field] = setTimeout(doUpdate, 600);
    }
  },[]);

  const upd=useCallback((id,f,v,immediate=true)=>persistField(id,f,v,immediate),[persistField]);

  const advSt=useCallback((id)=>{
    setIros(p=>{
      const item = p.find(i=>i.id===id);
      if(!item) return p;
      const nx=ST_M[item.st]?.nx||"unassessed";
      const updated = p.map(i=>{
        if(i.id!==id)return i;
        return {...i, st:nx, apBy:nx==="approved"?"You":i.apBy, apAt:nx==="approved"?new Date().toISOString().slice(0,10):i.apAt};
      });
      // Persist
      const newItem = updated.find(i=>i.id===id);
      if(newItem){
        apiFetch(`/api/v1/materiality/iros/${id}`,{method:"PUT",body:JSON.stringify(toAPI(newItem))}).catch(console.error);
      }
      return updated;
    });
  },[]);

  const addEv=useCallback((id,txt)=>{
    if(!txt)return;
    setIros(p=>{
      const updated = p.map(i=>i.id===id?{...i,ev:[...i.ev,txt]}:i);
      const item = updated.find(i=>i.id===id);
      if(item) apiFetch(`/api/v1/materiality/iros/${id}`,{method:"PUT",body:JSON.stringify({evidence:item.ev})}).catch(console.error);
      return updated;
    });
    setNewEvInput("");
  },[]);

  const rmEv=useCallback((id,idx)=>{
    setIros(p=>{
      const updated = p.map(i=>i.id===id?{...i,ev:i.ev.filter((_,j)=>j!==idx)}:i);
      const item = updated.find(i=>i.id===id);
      if(item) apiFetch(`/api/v1/materiality/iros/${id}`,{method:"PUT",body:JSON.stringify({evidence:item.ev})}).catch(console.error);
      return updated;
    });
  },[]);

  const addStk=useCallback((id,txt)=>{
    if(!txt)return;
    setIros(p=>{
      const updated = p.map(i=>i.id===id?{...i,stk:[...i.stk,txt]}:i);
      const item = updated.find(i=>i.id===id);
      if(item) apiFetch(`/api/v1/materiality/iros/${id}`,{method:"PUT",body:JSON.stringify({stakeholders:item.stk})}).catch(console.error);
      return updated;
    });
    setNewStkInput("");
  },[]);

  const rmStk=useCallback((id,idx)=>{
    setIros(p=>{
      const updated = p.map(i=>i.id===id?{...i,stk:i.stk.filter((_,j)=>j!==idx)}:i);
      const item = updated.find(i=>i.id===id);
      if(item) apiFetch(`/api/v1/materiality/iros/${id}`,{method:"PUT",body:JSON.stringify({stakeholders:item.stk})}).catch(console.error);
      return updated;
    });
  },[]);

  const addIRO=useCallback(()=>{
    if(!newName)return;
    const payload = {name:newName,pillar:newP,esrs:newE,type:newTy,impact_severity:0,impact_likelihood:0,financial_magnitude:0,financial_probability:0,owner:"",status:"unassessed",evidence:[],notes:"",stakeholders:[],approved_by:"",approved_at:""};
    apiFetch("/api/v1/materiality/iros",{method:"POST",body:JSON.stringify(payload)})
      .then(data=>{
        setIros(p=>[...p,fromAPI(data)]);
        setNewName("");setShowAdd(false);setSel(null);
      }).catch(console.error);
  },[newName,newP,newE,newTy]);

  const delIRO=useCallback((id)=>{
    apiFetch(`/api/v1/materiality/iros/${id}`,{method:"DELETE"})
      .then(()=>{ setIros(p=>p.filter(i=>i.id!==id)); if(sel===id)setSel(null); })
      .catch(console.error);
  },[sel]);

  const scored=useMemo(()=>iros.map(i=>{const is=i.iS*i.iL,fs=i.fM*i.fP;return{...i,iSc:is,fSc:fs,iMat:is>=THRESH.impact,fMat:fs>=THRESH.financial,dMat:is>=THRESH.impact||fs>=THRESH.financial}}),[iros]);
  const filtered=useMemo(()=>pilF?scored.filter(i=>i.p===pilF):scored,[scored,pilF]);
  const dp=sel?scored.find(i=>i.id===sel):null;

  const activeESRS=useMemo(()=>{const a={};Object.keys(ESRS).forEach(k=>{const r=scored.filter(i=>i.e===k);const m=r.filter(i=>i.dMat);a[k]={total:r.length,mat:m.length,on:m.length>0,iros:m}});return a},[scored]);
  const kpis=useMemo(()=>{const t=scored.length,as=scored.filter(i=>i.st!=="unassessed").length,dm=scored.filter(i=>i.dMat).length,ow=scored.filter(i=>i.ow).length,ev=scored.filter(i=>i.ev.length>0).length,un=scored.filter(i=>i.st==="unassessed").length,ap=scored.filter(i=>i.st==="approved").length,ea=Object.values(activeESRS).filter(v=>v.on).length;const fwT=scored.filter(i=>i.dMat).reduce((s,i)=>{const t=FW_T[i.e];return s+(t?.gri?.length||0)+(t?.issb?.length||0)+(t?.cdp?.length||0)+(t?.tcfd?.length||0)},0);return{t,as,dm,ow,ev,un,ap,ea,fwT}},[scored,activeESRS]);

  const views=[{id:"work",l:"Workbench",i:"⚡"},{id:"matrix",l:"Matrix",i:"🎯"},{id:"activation",l:"Activation",i:"📊"},{id:"audit",l:"Audit",i:"🛡️"}];

  if(loading) return <div style={{background:C.bg,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",color:C.muted,fontSize:14}}>Loading IRO register...</div>;

  // ═══ WORKBENCH ══════════════════════════════════════════════════
  const WorkView=()=>(
    <div style={{display:"grid",gridTemplateColumns:dp?"1fr 420px":"1fr",gap:12}}>
      {/* LEFT: List + Actions */}
      <div>
        {/* KPI Strip */}
        <div data-testid="kpi-strip" style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
          {[{l:"Total",v:kpis.t,c:C.text},{l:"Material",v:kpis.dm,c:C.amber},{l:"Approved",v:kpis.ap,c:C.emerald},{l:"Unscored",v:kpis.un,c:kpis.un>0?C.red:C.emerald},{l:"ESRS Active",v:`${kpis.ea}/10`,c:C.cyan},{l:"FW Triggers",v:kpis.fwT,c:C.purple}].map((k,i)=>(
            <div key={i} data-testid={`kpi-${k.l.toLowerCase().replace(/\s/g,"-")}`} style={{padding:"8px 10px",borderRadius:6,background:C.card,border:`1px solid ${k.c}15`,minWidth:70}}>
              <div style={{fontSize:16,fontWeight:800,color:k.c,lineHeight:1}}>{k.v}</div>
              <div style={{fontSize:7,color:C.dim,marginTop:1}}>{k.l}</div>
            </div>
          ))}
        </div>

        {/* Filters + Add */}
        <div data-testid="filter-bar" style={{display:"flex",gap:4,marginBottom:8,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:8,color:C.dim}}>FILTER:</span>
          {PILS.map(p=><Pl key={p.id} data-testid={`filter-${p.id}`} color={p.c} small onClick={()=>setPilF(pilF===p.id?null:p.id)} active={pilF===p.id}>{p.id}</Pl>)}
          <Pl data-testid="filter-unscored" color={C.red} small onClick={()=>setPilF(pilF==="unscored"?null:"unscored")} active={pilF==="unscored"}>Unscored</Pl>
          <Pl data-testid="filter-material" color={C.amber} small onClick={()=>setPilF(pilF==="material"?null:"material")} active={pilF==="material"}>Material</Pl>
          <div style={{flex:1}}/>
          <button data-testid="add-iro-btn" onClick={()=>setShowAdd(!showAdd)} style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${C.emerald}44`,background:C.emerald+"12",color:C.emerald,fontSize:9,fontWeight:600,cursor:"pointer"}}>+ Add IRO</button>
        </div>

        {/* Add Form */}
        {showAdd&&<div data-testid="add-iro-form" style={{padding:"10px 12px",borderRadius:7,background:C.surface,border:`1px solid ${C.emerald}22`,marginBottom:8}}>
          <div style={{fontSize:9,fontWeight:700,color:C.emerald,marginBottom:6}}>NEW IRO</div>
          <input data-testid="new-iro-name" value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Describe the impact, risk, or opportunity..." style={{width:"100%",padding:"7px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:10,outline:"none",boxSizing:"border-box",marginBottom:6}}/>
          <div style={{display:"flex",gap:6}}>
            <select data-testid="new-iro-pillar" value={newP} onChange={e=>{setNewP(e.target.value);setNewE(e.target.value==="E"?"E1":e.target.value==="S"?"S1":"G1")}} style={{flex:1,padding:"5px",borderRadius:4,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:9,outline:"none"}}>{PILS.map(p=><option key={p.id} value={p.id}>{p.l}</option>)}</select>
            <select data-testid="new-iro-esrs" value={newE} onChange={e=>setNewE(e.target.value)} style={{flex:1,padding:"5px",borderRadius:4,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:9,outline:"none"}}>{Object.entries(ESRS).filter(([_,v])=>v.p===newP).map(([k,v])=><option key={k} value={k}>{k} — {v.l}</option>)}</select>
            <select data-testid="new-iro-type" value={newTy} onChange={e=>setNewTy(e.target.value)} style={{flex:1,padding:"5px",borderRadius:4,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:9,outline:"none"}}><option value="impact">Impact</option><option value="risk">Risk</option><option value="opportunity">Opportunity</option></select>
            <button data-testid="create-iro-btn" onClick={addIRO} style={{padding:"5px 14px",borderRadius:4,border:"none",background:C.emerald,color:C.bg,fontSize:10,fontWeight:700,cursor:"pointer"}}>Create</button>
          </div>
        </div>}

        {/* IRO List */}
        <div data-testid="iro-list" style={{display:"flex",flexDirection:"column",gap:3}}>
          {(pilF==="unscored"?scored.filter(i=>i.st==="unassessed"):pilF==="material"?scored.filter(i=>i.dMat):filtered).map(i=>{
            const isSel=sel===i.id;const tc=i.ty==="impact"?C.red:i.ty==="risk"?C.amber:C.emerald;const es=ESRS[i.e];const sm=ST_M[i.st];
            const completeness=(i.ow?1:0)+(i.iS>0?1:0)+(i.fM>0?1:0)+(i.ev.length>0?1:0)+(i.stk.length>0?1:0);
            return(<div key={i.id} data-testid={`iro-item-${i.id}`} onClick={()=>setSel(isSel?null:i.id)} style={{
              padding:"9px 12px",borderRadius:7,cursor:"pointer",transition:"all .2s",
              background:isSel?tc+"08":C.card,border:`1px solid ${isSel?tc+"44":i.st==="unassessed"?C.red+"22":C.border}`,
            }}>
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                <span style={{fontSize:9,color:tc}}>{i.ty==="impact"?"●":i.ty==="risk"?"▲":"◆"}</span>
                <span style={{flex:1,fontSize:10,fontWeight:isSel?700:500,color:C.text}}>{i.n}</span>
                <Pl color={es?.c||C.dim} small>{i.e}</Pl>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <button data-testid={`status-btn-${i.id}`} onClick={e=>{e.stopPropagation();advSt(i.id)}} style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:8,fontWeight:600,padding:"2px 7px",borderRadius:4,cursor:"pointer",background:sm.c+"12",color:sm.c,border:`1px solid ${sm.c}33`}} title={`Click → ${ST_M[sm.nx]?.l}`}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:sm.c,boxShadow:i.st==="draft"?`0 0 4px ${sm.c}`:"none"}}/>{sm.l}
                </button>
                {i.iSc>0&&<span style={{fontSize:8,color:i.iMat?C.red:C.dim,fontWeight:700,background:(i.iMat?C.red:C.dim)+"10",padding:"1px 4px",borderRadius:3}}>I:{i.iSc}</span>}
                {i.fSc>0&&<span style={{fontSize:8,color:i.fMat?C.blue:C.dim,fontWeight:700,background:(i.fMat?C.blue:C.dim)+"10",padding:"1px 4px",borderRadius:3}}>F:{i.fSc}</span>}
                {i.dMat&&<span style={{fontSize:7,fontWeight:700,padding:"1px 4px",borderRadius:3,background:C.amber,color:C.bg}}>MATERIAL</span>}
                {i.activatedDPs>0&&<span data-testid={`graph-dp-${i.id}`} style={{fontSize:7,fontWeight:600,padding:"1px 5px",borderRadius:3,background:C.cyan+"15",color:C.cyan}}>{i.activatedDPs} DPs</span>}
                {i.triggeredESRS>0&&<span style={{fontSize:7,fontWeight:600,padding:"1px 5px",borderRadius:3,background:C.purple+"15",color:C.purple}}>{i.triggeredESRS} ESRS</span>}
                {i.triggeredFWReqs>0&&<span style={{fontSize:7,fontWeight:600,padding:"1px 5px",borderRadius:3,background:C.emerald+"15",color:C.emerald}}>{i.triggeredFWReqs} FWs</span>}
                <div style={{flex:1}}/>
                <div style={{display:"flex",gap:1}}>{[...Array(5)].map((_,j)=><div key={j} style={{width:4,height:10,borderRadius:1,background:j<completeness?C.emerald:C.dim+"44"}}/>)}</div>
                <span style={{fontSize:7,color:C.dim}}>{completeness}/5</span>
              </div>
            </div>);
          })}
        </div>
      </div>

      {/* RIGHT: Detail/Scoring Panel */}
      {dp&&<div data-testid="scoring-panel" style={{overflowY:"auto",maxHeight:"calc(100vh - 100px)"}}>
        {/* Header */}
        <div style={{padding:"12px 14px",borderRadius:8,background:`linear-gradient(135deg,${(dp.ty==="impact"?C.red:dp.ty==="risk"?C.amber:C.emerald)}08,transparent)`,border:`1px solid ${(dp.ty==="impact"?C.red:dp.ty==="risk"?C.amber:C.emerald)}33`,marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:4}}>
            <span style={{fontSize:10,color:dp.ty==="impact"?C.red:dp.ty==="risk"?C.amber:C.emerald}}>{dp.ty==="impact"?"● Impact":dp.ty==="risk"?"▲ Risk":"◆ Opportunity"}</span>
            <Pl color={ESRS[dp.e]?.c} small>{dp.e} — {ESRS[dp.e]?.l}</Pl>
          </div>
          <div style={{fontSize:13,fontWeight:800,color:C.text,marginBottom:6}}>{dp.n}</div>
          <div style={{display:"flex",gap:4,alignItems:"center"}}>
            <button data-testid="detail-status-btn" onClick={()=>advSt(dp.id)} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:9,fontWeight:600,padding:"4px 10px",borderRadius:5,cursor:"pointer",background:ST_M[dp.st].c+"15",color:ST_M[dp.st].c,border:`1px solid ${ST_M[dp.st].c}33`}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:ST_M[dp.st].c}}/>{ST_M[dp.st].l} → {ST_M[ST_M[dp.st].nx]?.l}
            </button>
            {dp.apBy&&<span style={{fontSize:8,color:C.emerald}}>Approved by {dp.apBy} on {dp.apAt}</span>}
            <button data-testid="delete-iro-btn" onClick={()=>delIRO(dp.id)} style={{marginLeft:"auto",padding:"3px 8px",borderRadius:4,border:`1px solid ${C.red}33`,background:"transparent",color:C.red,fontSize:8,cursor:"pointer"}}>Delete</button>
          </div>
        </div>

        {/* SCORING — Impact */}
        <div data-testid="impact-scoring" style={{padding:"10px 12px",borderRadius:7,background:C.red+"04",border:`1px solid ${C.red}18`,marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:10,fontWeight:700,color:C.red}}>IMPACT MATERIALITY</span>
            <span data-testid="impact-score" style={{fontSize:16,fontWeight:800,color:dp.iMat?C.red:C.dim}}>{dp.iSc}/25 {dp.iMat?"MATERIAL":""}</span>
          </div>
          <ScoreGrid data-testid="severity-grid" label="Severity (scale · scope · irremediability)" value={dp.iS} onChange={v=>upd(dp.id,"iS",v)} color={C.red} labels={SEV_LABELS}/>
          <ScoreGrid data-testid="likelihood-grid" label="Likelihood of occurrence" value={dp.iL} onChange={v=>upd(dp.id,"iL",v)} color={C.red} labels={LIK_LABELS}/>
          {dp.iS>0&&dp.iL>0&&<div data-testid="impact-verdict" style={{padding:"4px 8px",borderRadius:4,background:dp.iMat?C.red+"12":C.card,fontSize:8,color:dp.iMat?C.red:C.dim,textAlign:"center",fontWeight:600}}>
            {SEV_LABELS[dp.iS]} × {LIK_LABELS[dp.iL]} = {dp.iSc} {dp.iMat?`≥ ${THRESH.impact} → MATERIAL`:`< ${THRESH.impact} → not material`}
          </div>}
        </div>

        {/* SCORING — Financial */}
        <div data-testid="financial-scoring" style={{padding:"10px 12px",borderRadius:7,background:C.blue+"04",border:`1px solid ${C.blue}18`,marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:10,fontWeight:700,color:C.blue}}>FINANCIAL MATERIALITY</span>
            <span data-testid="financial-score" style={{fontSize:16,fontWeight:800,color:dp.fMat?C.blue:C.dim}}>{dp.fSc}/25 {dp.fMat?"MATERIAL":""}</span>
          </div>
          <ScoreGrid data-testid="magnitude-grid" label="Magnitude (effect on cash flows / value)" value={dp.fM} onChange={v=>upd(dp.id,"fM",v)} color={C.blue} labels={MAG_LABELS}/>
          <ScoreGrid data-testid="probability-grid" label="Probability of financial impact" value={dp.fP} onChange={v=>upd(dp.id,"fP",v)} color={C.blue} labels={PROB_LABELS}/>
          {dp.fM>0&&dp.fP>0&&<div data-testid="financial-verdict" style={{padding:"4px 8px",borderRadius:4,background:dp.fMat?C.blue+"12":C.card,fontSize:8,color:dp.fMat?C.blue:C.dim,textAlign:"center",fontWeight:600}}>
            {MAG_LABELS[dp.fM]} × {PROB_LABELS[dp.fP]} = {dp.fSc} {dp.fMat?`≥ ${THRESH.financial} → MATERIAL`:`< ${THRESH.financial} → not material`}
          </div>}
        </div>

        {/* VERDICT — live consequence preview */}
        <div data-testid="verdict-panel" style={{padding:"8px 12px",borderRadius:7,background:dp.dMat?C.amber+"08":C.emerald+"06",border:`1px solid ${dp.dMat?C.amber:C.emerald}22`,marginBottom:6}}>
          <div style={{fontSize:10,fontWeight:800,color:dp.dMat?C.amber:C.emerald,textAlign:"center",marginBottom:4}}>
            {dp.dMat?"DOUBLE MATERIALITY: MATERIAL":"NOT MATERIAL — document rationale below"}
          </div>
          {dp.dMat&&<>
            <div style={{fontSize:8,color:C.muted,textAlign:"center",marginBottom:4}}>This decision activates the following disclosures:</div>
            <div style={{display:"flex",gap:3,flexWrap:"wrap",justifyContent:"center"}}>
              <Pl color={C.cyan} small glow>ESRS {dp.e}</Pl>
              {FW_T[dp.e]?.gri?.map(g=><Pl key={g} color={C.emerald} small>{g}</Pl>)}
              {FW_T[dp.e]?.issb?.map(g=><Pl key={g} color={C.blue} small>{g}</Pl>)}
              {FW_T[dp.e]?.cdp?.map(g=><Pl key={g} color={C.amber} small>{g}</Pl>)}
              {FW_T[dp.e]?.tcfd?.map(g=><Pl key={g} color={C.blue} small>{g}</Pl>)}
            </div>
            <div style={{fontSize:8,color:C.purple,textAlign:"center",marginTop:4}}>
              = {1+(FW_T[dp.e]?.gri?.length||0)+(FW_T[dp.e]?.issb?.length||0)+(FW_T[dp.e]?.cdp?.length||0)+(FW_T[dp.e]?.tcfd?.length||0)} framework disclosures activated
            </div>
          </>}
        </div>

        {/* GRAPH CASCADE — live data from Neo4j */}
        {(dp.activatedDPs>0||dp.triggeredESRS>0||dp.triggeredFWReqs>0)&&<div data-testid="graph-cascade-panel" style={{padding:"8px 12px",borderRadius:7,background:C.cyan+"06",border:`1px solid ${C.cyan}22`,marginBottom:6}}>
          <div style={{fontSize:9,fontWeight:700,color:C.cyan,marginBottom:4,display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:C.cyan,boxShadow:`0 0 6px ${C.cyan}`}}/>
            GRAPH CASCADE (Neo4j Live)
          </div>
          <div style={{display:"flex",gap:6,justifyContent:"center"}}>
            <div style={{textAlign:"center",padding:"4px 10px",borderRadius:5,background:C.cyan+"10"}}>
              <div style={{fontSize:16,fontWeight:800,color:C.cyan}}>{dp.activatedDPs}</div>
              <div style={{fontSize:7,color:C.muted}}>Datapoints</div>
            </div>
            <div style={{textAlign:"center",padding:"4px 10px",borderRadius:5,background:C.purple+"10"}}>
              <div style={{fontSize:16,fontWeight:800,color:C.purple}}>{dp.triggeredESRS}</div>
              <div style={{fontSize:7,color:C.muted}}>ESRS Reqs</div>
            </div>
            <div style={{textAlign:"center",padding:"4px 10px",borderRadius:5,background:C.emerald+"10"}}>
              <div style={{fontSize:16,fontWeight:800,color:C.emerald}}>{dp.triggeredFWReqs}</div>
              <div style={{fontSize:7,color:C.muted}}>FW Reqs</div>
            </div>
          </div>
        </div>}

        {/* OWNER + TYPE */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
          <div>
            <div style={{fontSize:8,color:C.muted,marginBottom:2}}>Owner <span style={{color:dp.ow?C.emerald:C.red}}>{dp.ow?"✓":"required"}</span></div>
            <select data-testid="owner-select" value={dp.ow} onChange={e=>upd(dp.id,"ow",e.target.value)} style={{width:"100%",padding:"6px 8px",borderRadius:5,border:`1px solid ${dp.ow?C.emerald+"44":C.red+"44"}`,background:C.card,color:C.text,fontSize:10,outline:"none"}}>
              {OWNERS.map(o=><option key={o} value={o}>{o||"— Assign owner —"}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:8,color:C.muted,marginBottom:2}}>Type</div>
            <select data-testid="type-select" value={dp.ty} onChange={e=>upd(dp.id,"ty",e.target.value)} style={{width:"100%",padding:"6px 8px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:10,outline:"none"}}>
              <option value="impact">Impact</option><option value="risk">Risk</option><option value="opportunity">Opportunity</option>
            </select>
          </div>
        </div>
        <div style={{marginBottom:6}}>
          <div style={{fontSize:8,color:C.muted,marginBottom:2}}>Assessment rationale / notes</div>
          <textarea data-testid="notes-textarea" value={dp.no} onChange={e=>upd(dp.id,"no",e.target.value,false)} rows={2} placeholder="Why is this material or not? What evidence supports the scoring?" style={{width:"100%",padding:"6px 8px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:9,outline:"none",boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}}/>
        </div>

        {/* EVIDENCE */}
        <div data-testid="evidence-section" style={{padding:"8px 10px",borderRadius:6,background:C.surface,border:`1px solid ${C.teal}18`,marginBottom:6}}>
          <div style={{fontSize:9,fontWeight:700,color:C.teal,marginBottom:4}}>EVIDENCE — {dp.ev.length} documents <span style={{color:dp.ev.length>0?C.emerald:C.red}}>{dp.ev.length>0?"✓":"required"}</span></div>
          {dp.ev.map((e,j)=>(
            <div key={j} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 6px",borderRadius:3,background:C.card,marginBottom:2,fontSize:9}}>
              <span style={{color:C.teal}}>📎</span>
              <span style={{flex:1,color:C.text}}>{e}</span>
              <button data-testid={`rm-ev-${j}`} onClick={()=>rmEv(dp.id,j)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:9,padding:"0 3px"}}>✕</button>
            </div>
          ))}
          <div style={{display:"flex",gap:4,marginTop:4}}>
            <input data-testid="evidence-input" value={newEvInput} onChange={e=>setNewEvInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addEv(dp.id,newEvInput)} placeholder="Add evidence document..." style={{flex:1,padding:"4px 8px",borderRadius:4,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:9,outline:"none"}}/>
            <button data-testid="add-evidence-btn" onClick={()=>addEv(dp.id,newEvInput)} style={{padding:"4px 8px",borderRadius:4,border:`1px solid ${C.teal}44`,background:C.teal+"12",color:C.teal,fontSize:8,cursor:"pointer",fontWeight:600}}>+ Add</button>
          </div>
        </div>

        {/* STAKEHOLDERS */}
        <div data-testid="stakeholders-section" style={{padding:"8px 10px",borderRadius:6,background:C.surface,border:`1px solid ${C.pink}18`,marginBottom:6}}>
          <div style={{fontSize:9,fontWeight:700,color:C.pink,marginBottom:4}}>STAKEHOLDERS CONSULTED — {dp.stk.length}</div>
          <div style={{display:"flex",gap:3,flexWrap:"wrap",marginBottom:4}}>
            {dp.stk.map((s,j)=>(
              <span key={j} data-testid={`stk-chip-${j}`} onClick={()=>rmStk(dp.id,j)} style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:4,background:C.card,border:`1px solid ${C.pink}22`,fontSize:8,color:C.text,cursor:"pointer"}} title="Click to remove">{s} <span style={{color:C.red,fontSize:7}}>✕</span></span>
            ))}
          </div>
          <div style={{display:"flex",gap:4}}>
            <input data-testid="stakeholder-input" value={newStkInput} onChange={e=>setNewStkInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addStk(dp.id,newStkInput)} placeholder="Add stakeholder group..." style={{flex:1,padding:"4px 8px",borderRadius:4,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:9,outline:"none"}}/>
            <button data-testid="add-stakeholder-btn" onClick={()=>addStk(dp.id,newStkInput)} style={{padding:"4px 8px",borderRadius:4,border:`1px solid ${C.pink}44`,background:C.pink+"12",color:C.pink,fontSize:8,cursor:"pointer",fontWeight:600}}>+ Add</button>
          </div>
        </div>

        {/* COMPLETENESS */}
        <div data-testid="completeness-checklist" style={{padding:"8px 10px",borderRadius:6,background:C.surface,border:`1px solid ${C.purple}18`}}>
          <div style={{fontSize:9,fontWeight:700,color:C.purple,marginBottom:4}}>COMPLETENESS CHECKLIST</div>
          {[
            {l:"Owner assigned",ok:!!dp.ow},
            {l:"Impact dimension scored",ok:dp.iS>0&&dp.iL>0},
            {l:"Financial dimension scored",ok:dp.fM>0&&dp.fP>0},
            {l:"Evidence linked (≥1 document)",ok:dp.ev.length>0},
            {l:"Stakeholders documented",ok:dp.stk.length>0},
          ].map((ch,j)=>(
            <div key={j} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 6px",borderRadius:3,fontSize:9,marginBottom:1}}>
              <span style={{fontSize:11,color:ch.ok?C.emerald:C.red}}>{ch.ok?"✓":"○"}</span>
              <span style={{color:ch.ok?C.muted:C.text}}>{ch.l}</span>
            </div>
          ))}
        </div>
      </div>}

      {/* Empty state */}
      {!dp&&<div data-testid="empty-panel" style={{display:"flex",alignItems:"center",justifyContent:"center",color:C.dim,fontSize:11}}>
        <div style={{textAlign:"center",padding:30}}>
          <div style={{fontSize:28,marginBottom:6}}>👈</div>
          <div>Select an IRO to score it</div>
          <div style={{fontSize:9,color:C.muted,marginTop:4}}>Click any item to open the assessment panel</div>
        </div>
      </div>}
    </div>
  );

  // ═══ MATRIX ═════════════════════════════════════════════════════
  const MatrixView=()=>{
    const W=540,H=400,P=48,R=14,gW=W-P-R,gH=H-P-R;
    const data=scored.filter(i=>i.iSc>0||i.fSc>0);
    const tX=P+(THRESH.financial/25)*gW,tY=(H-P)-(THRESH.impact/25)*gH;
    return(<div data-testid="matrix-view">
      <div style={{display:"flex",gap:4,marginBottom:8}}>
        {PILS.map(p=><Pl key={p.id} data-testid={`matrix-filter-${p.id}`} color={p.c} small onClick={()=>setPilF(pilF===p.id?null:p.id)} active={pilF===p.id}>{p.l}</Pl>)}
      </div>
      <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>
        <svg width={W} height={H} style={{background:C.card,borderRadius:8,border:`1px solid ${C.border}`}}>
          {[.2,.4,.6,.8].map(p=><g key={p}><line x1={P+p*gW} y1={R} x2={P+p*gW} y2={H-P} stroke={C.border} strokeWidth=".5"/><line x1={P} y1={R+p*gH} x2={W-R} y2={R+p*gH} stroke={C.border} strokeWidth=".5"/></g>)}
          <line x1={tX} y1={R} x2={tX} y2={H-P} stroke={C.blue} strokeWidth="1.5" strokeDasharray="5 3" opacity=".4"/>
          <line x1={P} y1={tY} x2={W-R} y2={tY} stroke={C.red} strokeWidth="1.5" strokeDasharray="5 3" opacity=".4"/>
          <rect x={P} y={R} width={tX-P} height={tY-R} fill={C.purple} opacity=".025"/>
          <rect x={tX} y={R} width={W-R-tX} height={tY-R} fill={C.red} opacity=".02"/>
          <rect x={P} y={tY} width={tX-P} height={H-P-tY} fill={C.blue} opacity=".02"/>
          <text x={P+4} y={R+10} fill={C.purple} fontSize="7" fontWeight="700" opacity=".5">BOTH MATERIAL</text>
          <text x={tX+4} y={R+10} fill={C.red} fontSize="7" fontWeight="700" opacity=".5">IMPACT ONLY</text>
          <text x={P+4} y={tY+10} fill={C.blue} fontSize="7" fontWeight="700" opacity=".5">FINANCIAL ONLY</text>
          <text x={tX+4} y={tY+10} fill={C.dim} fontSize="7" fontWeight="700" opacity=".3">NOT MATERIAL</text>
          <line x1={P} y1={H-P} x2={W-R} y2={H-P} stroke={C.borderHi}/><line x1={P} y1={R} x2={P} y2={H-P} stroke={C.borderHi}/>
          <text x={W/2} y={H-4} fill={C.blue} fontSize="8" textAnchor="middle" fontWeight="600">Financial Score →</text>
          <text x={8} y={H/2} fill={C.red} fontSize="8" textAnchor="middle" fontWeight="600" transform={`rotate(-90 8 ${H/2})`}>← Impact Score</text>
          {(pilF&&pilF!=="unscored"&&pilF!=="material"?data.filter(i=>i.p===pilF):data).map(i=>{
            const x=P+(i.fSc/25)*gW,y=(H-P)-(i.iSc/25)*gH;const dc=i.ty==="impact"?C.red:i.ty==="risk"?C.amber:C.emerald;const s=sel===i.id;
            return(<g key={i.id} onClick={()=>setSel(s?null:i.id)} style={{cursor:"pointer"}}>
              {s&&<circle cx={x} cy={y} r={14} fill="none" stroke={dc} strokeWidth="1.5" opacity=".3"><animate attributeName="r" from="10" to="18" dur="1.5s" repeatCount="indefinite"/><animate attributeName="opacity" from=".3" to="0" dur="1.5s" repeatCount="indefinite"/></circle>}
              <circle cx={x} cy={y} r={s?9:6} fill={dc+(s?"44":"22")} stroke={dc} strokeWidth={s?2.5:1.5}/>
              <text x={x} y={y+3} textAnchor="middle" fill={dc} fontSize="7" fontWeight="700">{i.ty==="impact"?"●":i.ty==="risk"?"▲":"◆"}</text>
              {s&&<text x={x+12} y={y-3} fill={C.text} fontSize="7" fontWeight="600">{i.n.slice(0,28)}{i.n.length>28?"…":""}</text>}
            </g>);
          })}
        </svg>
      </div>
      {sel&&dp&&<div data-testid="matrix-detail-card" style={{padding:"10px 14px",borderRadius:7,background:C.surface,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
          <span style={{fontSize:12,fontWeight:800,color:C.text}}>{dp.n}</span>
          {dp.dMat?<span style={{fontSize:7,fontWeight:700,padding:"1px 5px",borderRadius:3,background:C.amber,color:C.bg}}>MATERIAL</span>:<span style={{fontSize:7,fontWeight:700,padding:"1px 5px",borderRadius:3,background:C.emerald,color:C.bg}}>NOT MAT.</span>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4,fontSize:9}}>
          <div style={{padding:"4px 6px",borderRadius:3,background:C.card}}><div style={{color:C.muted,fontSize:7}}>Impact</div><div style={{fontWeight:800,color:dp.iMat?C.red:C.dim}}>{dp.iSc}</div></div>
          <div style={{padding:"4px 6px",borderRadius:3,background:C.card}}><div style={{color:C.muted,fontSize:7}}>Financial</div><div style={{fontWeight:800,color:dp.fMat?C.blue:C.dim}}>{dp.fSc}</div></div>
          <div style={{padding:"4px 6px",borderRadius:3,background:C.card}}><div style={{color:C.muted,fontSize:7}}>ESRS</div><div style={{fontWeight:600,color:ESRS[dp.e]?.c}}>{dp.e}</div></div>
          <div style={{padding:"4px 6px",borderRadius:3,background:C.card}}><div style={{color:C.muted,fontSize:7}}>Owner</div><div style={{color:dp.ow?C.text:C.red}}>{dp.ow||"NONE"}</div></div>
          <div style={{padding:"4px 6px",borderRadius:3,background:C.card}}><div style={{color:C.muted,fontSize:7}}>Status</div><div style={{color:ST_M[dp.st].c}}>{ST_M[dp.st].l}</div></div>
        </div>
        <div style={{marginTop:6,textAlign:"center"}}><button data-testid="open-in-workbench-btn" onClick={()=>setView("work")} style={{padding:"4px 10px",borderRadius:4,border:`1px solid ${C.blue}44`,background:C.blue+"12",color:C.blue,fontSize:8,cursor:"pointer",fontWeight:600}}>Open in Workbench →</button></div>
      </div>}
    </div>);
  };

  // ═══ ACTIVATION ═════════════════════════════════════════════════
  const ActivationView=()=>(
    <div data-testid="activation-view">
      <div style={{textAlign:"center",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700}}>Standard Activation — Live from Materiality Scores</div>
        <div style={{fontSize:10,color:C.muted}}>{kpis.ea}/10 ESRS topical + {kpis.fwT} voluntary framework disclosures activated</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:6}}>
        {Object.entries(ESRS).map(([code,info])=>{
          const a=activeESRS[code];const triggers=FW_T[code];const allTriggers=[...(triggers?.gri||[]),...(triggers?.issb||[]),...(triggers?.cdp||[]),...(triggers?.tcfd||[])];
          return(<div key={code} data-testid={`activation-${code}`} style={{padding:"10px 14px",borderRadius:8,background:a.on?info.c+"06":C.card,border:`1px solid ${a.on?info.c+"44":C.border}`,opacity:a.on?1:.35,transition:"all .3s"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
              <span style={{fontSize:11,fontWeight:700,color:info.c}}>{code} — {info.l}</span>
              {a.on?<span style={{fontSize:7,fontWeight:700,padding:"1px 5px",borderRadius:3,background:info.c,color:C.bg}}>ACTIVE</span>:<span style={{fontSize:7,fontWeight:700,padding:"1px 5px",borderRadius:3,background:C.dim,color:C.bg}}>OFF</span>}
            </div>
            <Bar v={a.mat} mx={a.total||1} cl={info.c} h={4}/>
            <div style={{fontSize:8,color:C.muted,marginTop:3}}>{a.mat} material of {a.total} IROs</div>
            {a.on&&<div style={{marginTop:6}}>
              {a.iros.map(m=><div key={m.id} style={{fontSize:8,padding:"2px 5px",borderRadius:3,background:C.card,marginBottom:1,display:"flex",gap:3}}><span style={{color:m.ty==="impact"?C.red:m.ty==="risk"?C.amber:C.emerald}}>{m.ty==="impact"?"●":"▲"}</span><span style={{color:C.text,flex:1}}>{m.n}</span></div>)}
              <div style={{marginTop:4,display:"flex",gap:2,flexWrap:"wrap"}}>
                {allTriggers.map(t=><Pl key={t} color={info.c} small>{t}</Pl>)}
              </div>
            </div>}
            {!a.on&&a.total>0&&<div style={{marginTop:4,fontSize:8,color:C.dim}}>All IROs below threshold.</div>}
          </div>);
        })}
      </div>
    </div>
  );

  // ═══ AUDIT ══════════════════════════════════════════════════════
  const AuditView=()=>{
    const checks=[
      {l:"All IROs have owners",pct:Math.round(kpis.ow/kpis.t*100),c:kpis.ow===kpis.t?C.emerald:C.red,blockers:scored.filter(i=>!i.ow).map(i=>({n:i.n,id:i.id}))},
      {l:"All IROs scored (both dimensions)",pct:Math.round(scored.filter(i=>i.iS>0&&i.fM>0).length/kpis.t*100),c:scored.every(i=>i.iS>0&&i.fM>0)?C.emerald:C.amber,blockers:scored.filter(i=>i.iS===0||i.fM===0).map(i=>({n:i.n,id:i.id}))},
      {l:"All material IROs have evidence",pct:(()=>{const m=scored.filter(i=>i.dMat);const e=m.filter(i=>i.ev.length>0);return m.length?Math.round(e.length/m.length*100):100})(),c:C.blue,blockers:scored.filter(i=>i.dMat&&i.ev.length===0).map(i=>({n:i.n,id:i.id}))},
      {l:"All assessed IROs have notes",pct:(()=>{const a=scored.filter(i=>i.st!=="unassessed");const n=a.filter(i=>i.no);return a.length?Math.round(n.length/a.length*100):100})(),c:C.purple,blockers:scored.filter(i=>i.st!=="unassessed"&&!i.no).map(i=>({n:i.n,id:i.id}))},
      {l:"All 10 topical standards have ≥1 IRO",pct:Math.round(Object.keys(ESRS).filter(k=>scored.some(i=>i.e===k)).length/10*100),c:C.teal,blockers:Object.entries(ESRS).filter(([k])=>!scored.some(i=>i.e===k)).map(([k,v])=>({n:`${k}: ${v.l}`,id:null}))},
      {l:"Stakeholder input documented",pct:Math.round(scored.filter(i=>i.stk.length>0).length/kpis.t*100),c:C.pink,blockers:scored.filter(i=>i.stk.length===0).map(i=>({n:i.n,id:i.id}))},
    ];
    const overall=Math.round(checks.reduce((s,c)=>s+c.pct,0)/checks.length);
    const [openCheck,setOpenCheck]=useState(null);
    return(<div data-testid="audit-view">
      <div style={{padding:"16px",borderRadius:10,background:`linear-gradient(135deg,${overall>=80?C.emerald:overall>=50?C.amber:C.red}06,transparent)`,border:`1px solid ${overall>=80?C.emerald:overall>=50?C.amber:C.red}22`,marginBottom:12,textAlign:"center"}}>
        <div style={{fontSize:8,color:C.muted,letterSpacing:1,textTransform:"uppercase"}}>AUDIT READINESS</div>
        <div data-testid="audit-score" style={{fontSize:38,fontWeight:800,color:overall>=80?C.emerald:overall>=50?C.amber:C.red}}>{overall}%</div>
        <Bar v={overall} mx={100} cl={overall>=80?C.emerald:overall>=50?C.amber:C.red} h={6}/>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
        {checks.map((ch,j)=>(
          <div key={j}>
            <div data-testid={`audit-check-${j}`} onClick={()=>setOpenCheck(openCheck===j?null:j)} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:openCheck===j?"6px 6px 0 0":6,background:C.card,border:`1px solid ${ch.pct>=100?C.emerald+"22":ch.c+"18"}`,borderBottom:openCheck===j?"none":undefined,cursor:"pointer"}}>
              <span style={{fontSize:12,color:ch.pct>=100?C.emerald:ch.c}}>{ch.pct>=100?"✓":"○"}</span>
              <span style={{flex:1,fontSize:10,color:ch.pct>=100?C.muted:C.text}}>{ch.l}</span>
              <div style={{width:60}}><Bar v={ch.pct} mx={100} cl={ch.c} h={4}/></div>
              <span style={{fontSize:10,fontWeight:700,color:ch.c,minWidth:28,textAlign:"right"}}>{ch.pct}%</span>
              {ch.blockers.length>0&&<Pl color={C.red} small>{ch.blockers.length}</Pl>}
              <span style={{fontSize:9,color:C.dim,transform:openCheck===j?"rotate(180deg)":"rotate(0)",transition:"transform .2s"}}>▼</span>
            </div>
            {openCheck===j&&ch.blockers.length>0&&<div style={{padding:"8px 10px",border:`1px solid ${ch.c}18`,borderTop:"none",borderRadius:"0 0 6px 6px",background:C.surface}}>
              <div style={{fontSize:8,fontWeight:700,color:C.red,marginBottom:3}}>BLOCKERS — fix these to reach 100%:</div>
              {ch.blockers.map((b,k)=>(
                <div key={k} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 6px",borderRadius:3,background:C.card,marginBottom:2,fontSize:8}}>
                  <span style={{color:C.red}}>⚠</span>
                  <span style={{flex:1,color:C.text}}>{b.n}</span>
                  {b.id&&<button data-testid={`fix-btn-${b.id}`} onClick={()=>{setSel(b.id);setView("work")}} style={{padding:"1px 6px",borderRadius:3,border:`1px solid ${C.blue}44`,background:C.blue+"12",color:C.blue,fontSize:7,cursor:"pointer"}}>Fix →</button>}
                </div>
              ))}
            </div>}
          </div>
        ))}
      </div>
    </div>);
  };

  // ═══ RENDER ═════════════════════════════════════════════════════
  return(
    <div data-testid="materiality-os" style={{background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <div style={{padding:"10px 18px 6px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:10,background:C.bg+"f0",backdropFilter:"blur(12px)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <div style={{width:26,height:26,borderRadius:6,background:`linear-gradient(135deg,${C.teal},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:C.white}}>D</div>
          <span style={{fontSize:13,fontWeight:700}}>DisclosureOS</span>
          <span style={{color:C.dim}}>/</span>
          <span style={{fontSize:13,fontWeight:700,color:C.amber}}>Materiality Intelligence</span>
          <Pl data-testid="data-source-badge" color={dataSource === "graph" ? C.cyan : C.amber} small glow>{dataSource === "graph" ? "Neo4j" : "MongoDB"} · {iros.length} IROs</Pl>
          <div style={{flex:1}}/>
          <Pl color={C.amber} small glow>{kpis.dm} material</Pl>
          <Pl color={kpis.un>0?C.red:C.emerald} small>{kpis.un>0?`${kpis.un} unscored`:"✓ complete"}</Pl>
          <Pl color={C.cyan} small>{kpis.ea}/10 ESRS</Pl>
          <Pl color={C.purple} small>{kpis.fwT} FW triggers</Pl>
        </div>
        <div data-testid="view-tabs" style={{display:"flex",gap:3}}>
          {views.map(v=><button key={v.id} data-testid={`tab-${v.id}`} onClick={()=>setView(v.id)} style={{display:"flex",alignItems:"center",gap:3,padding:"4px 10px",borderRadius:5,fontSize:9,fontWeight:600,cursor:"pointer",border:`1px solid ${view===v.id?C.blue:C.border}`,background:view===v.id?C.blue+"15":"transparent",color:view===v.id?C.blue:C.muted}}>{v.i} {v.l}</button>)}
        </div>
      </div>
      <div style={{padding:"12px 18px 40px",maxWidth:1400,margin:"0 auto"}}>
        {view==="work"&&<WorkView/>}
        {view==="matrix"&&<MatrixView/>}
        {view==="activation"&&<ActivationView/>}
        {view==="audit"&&<AuditView/>}

        {/* Live User Journeys */}
        <JourneySection title="Live Journeys">
          <JourneyPanel title="Double Materiality Assessment (AI)" ucId="UC-03" defaultOpen><MaterialityAssessment /></JourneyPanel>
        </JourneySection>
      </div>
      <div style={{textAlign:"center",padding:"10px 18px 16px",borderTop:`1px solid ${C.border}`}}>
        <div style={{fontSize:11,fontWeight:300,color:C.muted,fontStyle:"italic"}}>"Enter data once. <span style={{color:C.emerald,fontWeight:600}}>Defend it everywhere.</span>"</div>
      </div>
    </div>
  );
}
